import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  HStack,
  Text,
  VStack,
  Spinner,
  Input,
  Badge,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import { leoTokens } from '../../theme/leoTokens';
import {
  csvWizardEndpoints,
  ICsvAnalyzeResponse,
  ICsvAthleteMatch,
  ICsvMappingEntryFE,
  ICsvPreviewResponse,
} from '../../../../endpoints/sessions';
import { ICatalogResponse } from '../../../../models/sessions';
import { SessionDataType } from '../../../../models/m6-2';
import { FileDropZone } from './FileDropZone';
import { ColumnMappingTable } from './ColumnMappingTable';
import { AthleteMatcherTable } from './AthleteMatcherTable';
import { PreviewTable } from './PreviewTable';
import CustomButton from '../../../../shared/CustomButton/CustomButton';
import CustomSelectInput from '../../../../shared/CustomSelectInput/CustomSelectInput';
import { DataTypeBadge } from '../m6-2/DataTypeBadge';
import { DataTypeFallbackChooser } from '../m6-2/DataTypeFallbackChooser';
import { CrossTeamDestinationSection } from '../m6-2/CrossTeamDestinationSection';
import { ZeroMatchBanner } from '../m6-2/ZeroMatchBanner';
import { useMyTeams } from '../../hooks/useMyTeams';

interface ImportCSVWizardProps {
  open: boolean;
  onClose: () => void;
  catalog: ICatalogResponse | undefined;
  teamId: string;
  rosterAthletes: { _id: string; firstName: string; lastName: string }[];
  onCompleted: () => void;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6;

interface WizardState {
  step: Step;
  file?: { name: string; base64: string };
  analyze?: ICsvAnalyzeResponse;
  fileMode: 'multi_athlete' | 'single_athlete';
  singleAthleteId?: string;
  mapping: ICsvMappingEntryFE[];
  templateApplied: boolean;
  athleteMatches?: ICsvAthleteMatch[];
  athleteAssignments: Record<string, string | null>;
  preview?: ICsvPreviewResponse;
  conflictStrategy: 'skip' | 'replace' | 'duplicate';
  saveTemplate: boolean;
  templateName: string;
  loading: boolean;
  error?: string;
  executeResult?: {
    rowsImported: number;
    rowsUpdated: number;
    rowsSkipped: number;
  };
  // M6.2 auto-detect + cross-team fields
  detectedDataType?: SessionDataType;
  detectConfidence?: number;
  needsManualChoice?: boolean;
  detectedTeamFromFile?: string | null;
  targetTeamId?: string;
  allowCreateNewAthletes?: boolean;
}

const initialState: WizardState = {
  step: 1,
  fileMode: 'multi_athlete',
  mapping: [],
  templateApplied: false,
  athleteAssignments: {},
  conflictStrategy: 'skip',
  saveTemplate: false,
  templateName: '',
  loading: false,
};

export const ImportCSVWizard: React.FC<ImportCSVWizardProps> = ({
  open,
  onClose,
  catalog,
  teamId,
  rosterAthletes,
  onCompleted,
}) => {
  const [s, setS] = useState<WizardState>(initialState);
  const myTeamsQuery = useMyTeams();

  useEffect(() => {
    if (!open) setS(initialState);
  }, [open]);

  const update = (patch: Partial<WizardState>) =>
    setS((prev) => ({ ...prev, ...patch }));

  // Helper: risolve il nome di un team per ID (da myTeams o fallback ID)
  const getTeamName = (tid?: string): string => {
    if (!tid) return teamId;
    const found = myTeamsQuery.data?.find((t) => t._id === tid);
    return found?.name ?? tid;
  };

  // Nome del team corrente (context wizard)
  const currentTeamName = getTeamName(teamId);

  // ─── Step 1: upload + analyze
  const handleFileSelected = async (file: File, base64: string) => {
    update({ file: { name: file.name, base64 }, loading: true, error: undefined });
    try {
      const res = await csvWizardEndpoints.analyze({
        fileBase64: base64,
        filename: file.name,
        teamId,
      });
      const analyze = res.data;

      // Applica template se trovato (auto-learning)
      let mapping: ICsvMappingEntryFE[];
      let templateApplied = false;
      if (analyze.templateMatch) {
        mapping = analyze.templateMatch.mapping;
        templateApplied = true;
      } else {
        // Inizia con suggestions keyword-based
        mapping = analyze.columns.map((col, idx) => ({
          csvColumnIndex: idx,
          csvColumnName: col,
          leopartMetricId: analyze.suggestions[col] ?? '',
          ignored: false,
        }));
      }

      update({
        analyze,
        mapping,
        templateApplied,
        loading: false,
        step: 2,
        // M6.2 auto-detect fields from analyze response
        detectedDataType: analyze.detectedDataType,
        detectConfidence: analyze.detectConfidence,
        needsManualChoice: analyze.needsManualChoice,
        detectedTeamFromFile: analyze.detectedTeamFromFile,
        targetTeamId: teamId,
      });
    } catch (e: any) {
      update({
        loading: false,
        error: e?.response?.data?.message ?? e?.message ?? 'Errore analisi',
      });
    }
  };

  // ─── Step 4: match atleti
  const matchAthletesIfNeeded = async () => {
    if (s.fileMode !== 'multi_athlete' || !s.analyze || !s.file) return;
    const athleteEntry = s.mapping.find(
      (m) => m.leopartMetricId === 'athlete' && !m.ignored,
    );
    if (!athleteEntry) {
      update({ error: 'Mappa una colonna come "athlete"' });
      return;
    }
    update({ loading: true, error: undefined });
    try {
      const res = await csvWizardEndpoints.matchAthletes({
        fileBase64: s.file.base64,
        encoding: s.analyze.structure.encoding,
        separator: s.analyze.structure.separator,
        quoteChar: s.analyze.structure.quoteChar,
        decimalSeparator: s.analyze.structure.decimalSeparator,
        thousandSeparator: s.analyze.structure.thousandSeparator,
        headerRow: s.analyze.structure.headerRow,
        athleteColumnIndex: athleteEntry.csvColumnIndex,
        teamId,
      });
      // Pre-popola assignments con match esatti+alias automatici
      const initialAssignments: Record<string, string | null> = {};
      for (const m of res.data.matches) {
        if (m.matchType === 'exact' || m.matchType === 'alias') {
          initialAssignments[m.csvValue] = m.matchedAthleteId ?? null;
        }
      }
      update({
        athleteMatches: res.data.matches,
        athleteAssignments: initialAssignments,
        loading: false,
        step: 4,
      });
    } catch (e: any) {
      update({
        loading: false,
        error: e?.response?.data?.message ?? e?.message ?? 'Errore matching',
      });
    }
  };

  // ─── Step 5: preview
  const generatePreview = async () => {
    if (!s.analyze || !s.file) return;
    update({ loading: true, error: undefined });
    try {
      const athleteAssignments = Object.entries(s.athleteAssignments).map(
        ([csvValue, athleteId]) => ({ csvValue, athleteId }),
      );
      const res = await csvWizardEndpoints.preview({
        fileBase64: s.file.base64,
        filename: s.file.name,
        encoding: s.analyze.structure.encoding,
        separator: s.analyze.structure.separator,
        quoteChar: s.analyze.structure.quoteChar,
        decimalSeparator: s.analyze.structure.decimalSeparator,
        thousandSeparator: s.analyze.structure.thousandSeparator,
        headerRow: s.analyze.structure.headerRow,
        fileMode: s.fileMode,
        singleAthleteId: s.singleAthleteId,
        mapping: s.mapping,
        athleteAssignments,
        teamId: s.targetTeamId ?? teamId,
      });
      update({ preview: res.data, loading: false, step: 5 });
    } catch (e: any) {
      update({
        loading: false,
        error: e?.response?.data?.message ?? e?.message ?? 'Errore preview',
      });
    }
  };

  // ─── Step 6: execute
  const executeImport = async () => {
    if (!s.analyze || !s.file) return;
    update({ loading: true, error: undefined });
    try {
      const athleteAssignments = Object.entries(s.athleteAssignments).map(
        ([csvValue, athleteId]) => ({ csvValue, athleteId }),
      );
      const res = await csvWizardEndpoints.execute({
        fileBase64: s.file.base64,
        filename: s.file.name,
        encoding: s.analyze.structure.encoding,
        separator: s.analyze.structure.separator,
        quoteChar: s.analyze.structure.quoteChar,
        decimalSeparator: s.analyze.structure.decimalSeparator,
        thousandSeparator: s.analyze.structure.thousandSeparator,
        headerRow: s.analyze.structure.headerRow,
        fileMode: s.fileMode,
        singleAthleteId: s.singleAthleteId,
        mapping: s.mapping,
        athleteAssignments,
        teamId: s.targetTeamId ?? teamId,
        conflictStrategy: s.conflictStrategy,
        saveTemplate: s.saveTemplate,
        templateName: s.templateName,
      });
      update({
        loading: false,
        step: 6,
        executeResult: {
          rowsImported: res.data.rowsImported,
          rowsUpdated: res.data.rowsUpdated,
          rowsSkipped: res.data.rowsSkipped,
        },
      });
      onCompleted();
    } catch (e: any) {
      update({
        loading: false,
        error: e?.response?.data?.message ?? e?.message ?? 'Errore execute',
      });
    }
  };

  // ─── Validation per step (hook DEVE stare prima del return condizionale)
  const canGoNext = useMemo(() => {
    if (s.loading) return false;
    if (s.step === 1) return !!s.analyze;
    if (s.step === 2) {
      if (s.fileMode === 'single_athlete' && !s.singleAthleteId) return false;
      return true;
    }
    if (s.step === 3) {
      // Almeno una colonna mappata oltre a quelle obbligatorie
      const hasAthlete =
        s.fileMode === 'single_athlete' ||
        s.mapping.some((m) => m.leopartMetricId === 'athlete' && !m.ignored);
      const hasDate = s.mapping.some(
        (m) => m.leopartMetricId === 'date' && !m.ignored,
      );
      const hasMetric = s.mapping.some(
        (m) =>
          !m.ignored &&
          m.leopartMetricId &&
          !['athlete', 'date', 'session_type'].includes(m.leopartMetricId),
      );
      return hasAthlete && hasDate && hasMetric;
    }
    if (s.step === 4) {
      return Object.values(s.athleteAssignments).filter(Boolean).length > 0;
    }
    if (s.step === 5) return !!s.preview;
    return true;
  }, [s]);

  // ─── Step navigation
  const handleNext = async () => {
    if (s.step === 1) update({ step: 2 });
    else if (s.step === 2) update({ step: 3 });
    else if (s.step === 3) {
      if (s.fileMode === 'multi_athlete') await matchAthletesIfNeeded();
      else await generatePreview();
    } else if (s.step === 4) await generatePreview();
    else if (s.step === 5) await executeImport();
  };

  if (!open) return null;

  const handleBack = () => {
    if (s.step === 1) return;
    if (s.step === 5 && s.fileMode === 'single_athlete') {
      update({ step: 3 });
      return;
    }
    update({ step: (s.step - 1) as Step });
  };

  const athleteOptions = [
    { value: '', label: '— scegli atleta —' },
    ...rosterAthletes.map((a) => ({
      value: a._id,
      label: `${a.firstName} ${a.lastName}`,
    })),
  ];

  const conflictOptions: { value: 'skip' | 'replace' | 'duplicate'; label: string }[] = [
    { value: 'skip', label: 'Mantieni esistenti' },
    { value: 'replace', label: 'Sostituisci tutti' },
    { value: 'duplicate', label: 'Importa duplicati' },
  ];

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="blackAlpha.500"
      zIndex={1100}
      display="flex"
      alignItems="center"
      justifyContent="center"
      onClick={onClose}
    >
      <Box
        bg="var(--c-card-bg)"
        borderRadius="lg"
        width="90%"
        maxWidth="1100px"
        maxHeight="90vh"
        overflow="auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header + progress */}
        <Box
          p={4}
          borderBottom="1px solid"
          borderColor="gray.200"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Text fontSize="xl" fontWeight="700">
              Wizard Import CSV
            </Text>
            <Text fontSize="sm" color="gray.500">
              Step {s.step} di 6
            </Text>
          </Box>
          <CustomButton color="text" onClick={onClose}>
            ✕
          </CustomButton>
        </Box>

        {/* Step indicator */}
        {(() => {
          const STEPS = [
            { id: 1, label: 'Upload' },
            { id: 2, label: 'Struttura' },
            { id: 3, label: 'Mapping' },
            { id: 4, label: 'Atleti' },
            { id: 5, label: 'Preview' },
            { id: 6, label: 'Done' },
          ];
          return (
            <Box px={6} py={4} borderBottom="1px solid" borderColor="gray.100" bg="gray.50">
              <HStack gap={0} justify="space-between" alignItems="flex-start">
                {STEPS.map((step, idx) => (
                  <React.Fragment key={step.id}>
                    <VStack gap={1.5} flex={idx < STEPS.length - 1 ? undefined : undefined} minW="44px" alignItems="center">
                      <Box
                        w="32px"
                        h="32px"
                        borderRadius="50%"
                        bg={
                          step.id < s.step
                            ? 'green.500'
                            : step.id === s.step
                            ? leoTokens.brand
                            : 'white'
                        }
                        border="2px solid"
                        borderColor={
                          step.id < s.step
                            ? 'green.500'
                            : step.id === s.step
                            ? leoTokens.brandBorder
                            : leoTokens.divider
                        }
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color={
                          step.id < s.step
                            ? 'white'
                            : step.id === s.step
                            ? leoTokens.brandInk
                            : leoTokens.muted
                        }
                        fontSize="13px"
                        fontWeight={700}
                        style={{ transition: 'all 0.2s' }}
                        flexShrink={0}
                      >
                        {step.id < s.step ? (
                          <Check size={15} strokeWidth={2.5} />
                        ) : (
                          step.id
                        )}
                      </Box>
                      <Text
                        fontSize="10px"
                        fontWeight={step.id === s.step ? 700 : 500}
                        color={step.id <= s.step ? leoTokens.ink : leoTokens.muted}
                        whiteSpace="nowrap"
                        textAlign="center"
                        lineHeight="1.2"
                      >
                        {step.label}
                      </Text>
                    </VStack>
                    {idx < STEPS.length - 1 && (
                      <Box
                        flex={1}
                        h="2px"
                        bg={step.id < s.step ? 'green.500' : leoTokens.divider}
                        mt="15px"
                        mx={1}
                      />
                    )}
                  </React.Fragment>
                ))}
              </HStack>
            </Box>
          );
        })()}

        {/* Body */}
        <Box p={5} minH="400px">
          {s.error && (
            <Box
              bg="red.50"
              border="1px solid"
              borderColor="red.300"
              p={3}
              borderRadius="md"
              mb={3}
            >
              <Text color="red.700" fontSize="sm">
                ⚠ {s.error}
              </Text>
            </Box>
          )}

          {s.loading && (
            <Box textAlign="center" py={6}>
              <Spinner />
              <Text mt={2} color="gray.500">
                Elaborazione…
              </Text>
            </Box>
          )}

          {!s.loading && s.step === 1 && (
            <FileDropZone onFileSelected={handleFileSelected} />
          )}

          {!s.loading && s.step === 2 && s.analyze && (
            <VStack alignItems="stretch" gap={4}>
              <Text fontWeight="600">
                Struttura rilevata · file <em>{s.analyze.filename}</em>
              </Text>
              <Box bg="gray.50" p={3} borderRadius="md" fontSize="sm">
                <Text>
                  Encoding: <b>{s.analyze.structure.encoding}</b> · Separatore:{' '}
                  <b>{s.analyze.structure.separator === '\t' ? 'TAB' : s.analyze.structure.separator}</b> ·
                  Decimal: <b>{s.analyze.structure.decimalSeparator}</b> ·{' '}
                  Header: riga <b>{s.analyze.structure.headerRow}</b>
                </Text>
                <Text mt={1}>
                  Colonne: <b>{s.analyze.columns.length}</b> · Righe dati:{' '}
                  <b>{s.analyze.totalDataRows}</b>
                </Text>
                {s.analyze.templateMatch && (
                  <Badge mt={2} colorPalette="green">
                    ✓ Template trovato: {s.analyze.templateMatch.templateName}
                  </Badge>
                )}
              </Box>

              {/* M6.2 · auto-detect badge tipo dato */}
              {s.detectedDataType && (
                <HStack gap={2} alignItems="center">
                  <Text fontSize="sm" color="gray.600">Tipo dato rilevato:</Text>
                  <DataTypeBadge
                    dataType={s.detectedDataType}
                    confidence={s.detectConfidence}
                  />
                </HStack>
              )}

              {/* M6.2 · fallback chooser quando confidence bassa */}
              {s.needsManualChoice && (
                <DataTypeFallbackChooser
                  onChoose={(dt) =>
                    update({ detectedDataType: dt, needsManualChoice: false })
                  }
                />
              )}

              <Text fontWeight="600">Tipo struttura file</Text>
              <HStack gap={3}>
                <Box
                  flex="1"
                  border="2px solid"
                  borderColor={
                    s.fileMode === 'multi_athlete' ? 'green.500' : 'gray.200'
                  }
                  bg={s.fileMode === 'multi_athlete' ? 'green.50' : 'white'}
                  p={4}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => update({ fileMode: 'multi_athlete' })}
                >
                  <Text fontWeight="700">Multi-atleta</Text>
                  <Text fontSize="sm" color="gray.500">
                    Una colonna identifica l'atleta (export squadra/team)
                  </Text>
                </Box>
                <Box
                  flex="1"
                  border="2px solid"
                  borderColor={
                    s.fileMode === 'single_athlete' ? 'green.500' : 'gray.200'
                  }
                  bg={s.fileMode === 'single_athlete' ? 'green.50' : 'white'}
                  p={4}
                  borderRadius="md"
                  cursor="pointer"
                  onClick={() => update({ fileMode: 'single_athlete' })}
                >
                  <Text fontWeight="700">Singolo atleta</Text>
                  <Text fontSize="sm" color="gray.500">
                    Tutte righe stesso atleta (export Strava personale)
                  </Text>
                </Box>
              </HStack>

              {s.fileMode === 'single_athlete' && (
                <Box>
                  <Text fontSize="sm" fontWeight="600" mb={1}>
                    Atleta da assegnare
                  </Text>
                  <CustomSelectInput
                    options={athleteOptions}
                    value={s.singleAthleteId ?? ''}
                    onChange={(val: string) => update({ singleAthleteId: val })}
                    placeholder="— scegli atleta —"
                    menuPosition="fixed"
                  />
                </Box>
              )}
            </VStack>
          )}

          {!s.loading && s.step === 3 && s.analyze && (
            <VStack alignItems="stretch" gap={3}>
              {s.templateApplied && (
                <Box
                  bg="green.50"
                  border="1px solid"
                  borderColor="green.300"
                  p={3}
                  borderRadius="md"
                >
                  <Text color="green.700" fontSize="sm">
                    ✓ Template <b>{s.analyze.templateMatch?.templateName}</b>{' '}
                    applicato automaticamente. Modifica il mapping qui sotto se serve.
                  </Text>
                </Box>
              )}

              {/* M6.2 · cross-team destination quando file dichiara team diverso */}
              {s.detectedTeamFromFile &&
                s.detectedTeamFromFile.toLowerCase().trim() !==
                  currentTeamName.toLowerCase().trim() && (
                <CrossTeamDestinationSection
                  detectedTeamFromFile={s.detectedTeamFromFile}
                  currentTeamId={teamId}
                  currentTeamName={currentTeamName}
                  coachTeams={myTeamsQuery.data ?? []}
                  selectedTargetTeamId={s.targetTeamId ?? teamId}
                  onChangeTargetTeam={(id) => update({ targetTeamId: id })}
                />
              )}

              <ColumnMappingTable
                columns={s.analyze.columns}
                sampleRows={s.analyze.sampleRows}
                mapping={s.mapping}
                onChange={(m) => update({ mapping: m })}
                catalog={catalog}
                suggestions={s.analyze.suggestions}
                templateApplied={s.templateApplied}
              />
            </VStack>
          )}

          {!s.loading && s.step === 4 && s.athleteMatches && (
            <VStack alignItems="stretch" gap={3}>
              <Text fontSize="sm" color="gray.600">
                Conferma il match per ogni atleta del CSV. Match fuzzy
                richiedono verifica.
              </Text>
              <AthleteMatcherTable
                matches={s.athleteMatches}
                roster={rosterAthletes}
                assignments={s.athleteAssignments}
                onAssignmentChange={(csvValue, athleteId) =>
                  update({
                    athleteAssignments: {
                      ...s.athleteAssignments,
                      [csvValue]: athleteId,
                    },
                  })
                }
              />
            </VStack>
          )}

          {!s.loading && s.step === 5 && s.preview && (
            <VStack alignItems="stretch" gap={3}>
              {/* M6.2 · zero-match banner quando 0 atleti trovati nel team scelto */}
              {s.athleteMatches &&
                s.athleteMatches.length > 0 &&
                s.athleteMatches.filter((m) => m.matchedAthleteId).length === 0 && (
                <ZeroMatchBanner
                  matchedCount={0}
                  totalCount={s.athleteMatches.length}
                  targetTeamName={getTeamName(s.targetTeamId ?? teamId)}
                  onGoBackToStep3={() =>
                    update({ step: 3, athleteMatches: undefined, preview: undefined })
                  }
                  onCreateNewAthletes={() =>
                    update({ allowCreateNewAthletes: true })
                  }
                  onCancel={onClose}
                />
              )}

              <PreviewTable preview={s.preview} />

              {s.preview.totals.conflicts > 0 && (
                <Box
                  bg="orange.50"
                  border="1px solid"
                  borderColor="orange.300"
                  p={3}
                  borderRadius="md"
                >
                  <Text fontSize="sm" fontWeight="600" mb={2}>
                    Strategia conflitti ({s.preview.totals.conflicts} rilevati)
                  </Text>
                  <HStack gap={2}>
                    {conflictOptions.map((opt) => (
                      <CustomButton
                        key={opt.value}
                        color={s.conflictStrategy === opt.value ? 'primary' : 'outline'}
                        onClick={() => update({ conflictStrategy: opt.value })}
                      >
                        {opt.label}
                      </CustomButton>
                    ))}
                  </HStack>
                </Box>
              )}

              <Box bg="gray.50" p={3} borderRadius="md">
                <HStack>
                  <input
                    type="checkbox"
                    checked={s.saveTemplate}
                    onChange={(e) =>
                      update({ saveTemplate: e.target.checked })
                    }
                  />
                  <Text fontSize="sm">
                    Salva mapping come template (auto-learning prossimi import)
                  </Text>
                </HStack>
                {s.saveTemplate && (
                  <Input
                    mt={2}
                    placeholder="Nome template (es. Standard U15)"
                    value={s.templateName}
                    onChange={(e) => update({ templateName: e.target.value })}
                  />
                )}
              </Box>
            </VStack>
          )}

          {!s.loading && s.step === 6 && s.executeResult && (
            <Box textAlign="center" py={8}>
              <Text fontSize="3xl" mb={3}>
                ✅
              </Text>
              <Text fontSize="xl" fontWeight="700" mb={4}>
                Import completato
              </Text>
              <HStack justifyContent="center" gap={4}>
                <Badge colorPalette="green" fontSize="md" px={3} py={2}>
                  {s.executeResult.rowsImported} create
                </Badge>
                {s.executeResult.rowsUpdated > 0 && (
                  <Badge colorPalette="blue" fontSize="md" px={3} py={2}>
                    {s.executeResult.rowsUpdated} aggiornate
                  </Badge>
                )}
                {s.executeResult.rowsSkipped > 0 && (
                  <Badge colorPalette="gray" fontSize="md" px={3} py={2}>
                    {s.executeResult.rowsSkipped} saltate
                  </Badge>
                )}
              </HStack>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          p={4}
          borderTop="1px solid"
          borderColor="gray.200"
          display="flex"
          justifyContent="space-between"
          bg="gray.50"
        >
          <CustomButton
            color="outline"
            onClick={handleBack}
            disabled={s.step === 1 || s.loading || s.step === 6}
          >
            ← Indietro
          </CustomButton>
          <HStack gap={2}>
            <CustomButton color="text" onClick={onClose}>
              {s.step === 6 ? 'Chiudi' : 'Annulla'}
            </CustomButton>
            {s.step < 6 && (
              <CustomButton
                color="primary"
                onClick={handleNext}
                disabled={!canGoNext}
                isLoading={s.loading}
              >
                {s.step === 5 ? 'Conferma e importa' : 'Avanti →'}
              </CustomButton>
            )}
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};
